import React, { useEffect } from 'react';
import { TextField, CircularProgress, Alert, Box, Autocomplete } from '@mui/material';
import { useJobRoles } from '../hooks/useFilterOptions';
import type { JobRole } from '../api/types';

interface JobRoleSelectProps {
  value: string[];
  onChange: (values: string[]) => void;
  regionIds?: string[];
  depotIds?: string[];
  departmentIds?: string[];
}

export const JobRoleSelect: React.FC<JobRoleSelectProps> = ({
  value,
  onChange,
  regionIds,
  depotIds,
  departmentIds,
}) => {
  const { data, isLoading, error } = useJobRoles({
    regionIds,
    depotIds,
    departmentIds,
  });

  const options = data?.data || [];

  useEffect(() => {
    if (!options.length) return;
    const validIds = new Set(options.map((r) => r.id));
    const filtered = value.filter((id) => validIds.has(id));
    if (filtered.length !== value.length) {
      onChange(filtered);
    }
  }, [options]);

  if (error) {
    return <Alert severity='error'>Failed to load job roles. Please try again.</Alert>;
  }

  const selectedRoles = options.filter((role) => value.includes(role.id));

  const handleChange = (_event: unknown, newValue: JobRole[]) => {
    onChange(newValue.map((role) => role.id));
  };

  return (
    <Box>
      <Autocomplete
        multiple
        fullWidth
        options={options}
        value={selectedRoles}
        onChange={handleChange}
        getOptionLabel={(option) => option.name}
        loading={isLoading}
        filterSelectedOptions
        renderInput={(params) => (
          <TextField
            {...params}
            label='Select Job Roles'
            placeholder='Search job roles...'
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color='inherit' size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};
